import { useEffect } from 'react';
import { noteDirectionalInput } from './focus-memory';
import {
  type Direction,
  type Rect,
  controlKind,
  directionOfKey,
  ownsArrow,
  pickInDirection,
} from './spatial';

/**
 * Nawigacja strzałkami po tym, co przeglądarka uznaje za fokusowalne.
 *
 * Pilot telewizora wysyła zwykłe strzałki i Enter — nic więcej. Przeglądarka nie
 * przenosi po nich fokusu (robi to tylko Tab), więc bez tego haka aplikacja jest
 * **nieobsługiwalna pilotem**: da się przewinąć stronę, ale nie wybrać kafla.
 *
 * Działa na żywym drzewie DOM zamiast na rejestrze komponentów, więc obejmuje
 * ekrany już napisane i te, których jeszcze nie ma. To jest cała różnica wobec
 * biblioteki nawigacji przestrzennej z pierwotnego planu: tam każdy kafel
 * i przycisk musiałby dostać własny hak, tu nie musi żaden.
 *
 * Trzy sytuacje, w których hak **stoi z boku**, bo strzałka znaczy tam co innego:
 *
 *  1. **Pola tekstowe, suwaki i listy wyboru** — strzałka przesuwa kursor, zmienia
 *     wartość albo pozycję na liście. Przechwycenie odebrałoby edycję. Rozstrzygamy
 *     to **per kierunek** (`ownsArrow`), bo w polu jednoliniowym `↑`/`↓` nie robią
 *     nic i są jedynym wyjściem z niego dostępnym pilotem.
 *  2. **Odtwarzacz** — tam strzałki przewijają materiał i zmieniają głośność.
 *     Rozpoznajemy go po `data-immersive` na `<html>`, czyli po tym samym znaczniku,
 *     którym odtwarzacz wygasza chrome aplikacji.
 *  3. **Modyfikatory** — `Ctrl`/`Alt`/`Meta` ze strzałką to skróty systemu i
 *     przeglądarki (przewijanie do końca, przełączanie kart).
 */

/**
 * Co uznajemy za cel.
 *
 * Bez `[tabindex="-1"]`: taki element jest fokusowalny programowo, ale świadomie
 * wyjęty z kolejności — zwykle jest kontenerem, na który nikt nie chce wejść.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Czy ta strzałka należy do kontrolki, na której stoi fokus.
 *
 * Cała decyzja siedzi w `spatial.ts` jako funkcja na prymitywach — tutaj zostaje
 * samo odczytanie znacznika, typu i edytowalności z elementu.
 */
function ownsArrows(element: Element | null, direction: Direction): boolean {
  if (!(element instanceof HTMLElement)) return false;
  const inputType = element instanceof HTMLInputElement ? element.type : undefined;
  const kind = controlKind(element.tagName.toLowerCase(), inputType, element.isContentEditable);
  return ownsArrow(kind, direction);
}

export function useSpatialNavigation(): void {
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.ctrlKey || event.altKey || event.metaKey) return;
      const direction = directionOfKey(event.key);
      if (direction === null) return;

      /**
       * Strzałka już obsłużona nie jest nasza.
       *
       * Nasłuch siedzi na `window`, czyli na końcu drogi zdarzenia — menu, lista
       * wyboru i karuzela Radiksa chodzą po swoich pozycjach same i zaznaczają to
       * przez `preventDefault()`. Bez tego warunku jedno naciśnięcie robiłoby dwie
       * rzeczy: Radix przechodził na następną pozycję, a nawigacja wynosiła fokus
       * z otwartej warstwy gdzieś na stronę.
       */
      if (event.defaultPrevented) return;

      // Odtwarzacz ma własne znaczenie strzałek — patrz komentarz u góry.
      if (document.documentElement.dataset.immersive === 'true') return;

      const active = document.activeElement;
      if (ownsArrows(active, direction)) return;

      const elements = [...document.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(isVisible);
      if (elements.length === 0) return;

      /**
       * Bez elementu bieżącego zaczynamy od pierwszego widocznego.
       *
       * Dzieje się to po wejściu na ekran, zanim cokolwiek dostanie fokus: pilot
       * naciska „w dół", a fokus nie ma od czego liczyć kierunku. Skok na pierwszy
       * element jest tu jedyną odpowiedzią, która prowadzi dokądkolwiek.
       */
      if (!(active instanceof HTMLElement) || !elements.includes(active)) {
        elements[0]?.focus();
        event.preventDefault();
        return;
      }

      const from = toRect(active);
      const candidates = elements.filter((element) => element !== active);
      const index = pickInDirection(from, candidates.map(toRect), direction);
      if (index === null) return;

      const target = candidates[index];
      if (target === undefined) return;

      event.preventDefault();
      /**
       * Stąd wiadomo, że widz posługuje się strzałkami.
       *
       * Pad wysyła syntetyczne `keydown`, więc pilot, pad i klawiatura przechodzą tym
       * jednym miejscem — pamięć fokusu (`use-focus-memory.ts`) nie musi znać żadnego
       * z tych urządzeń osobno.
       */
      noteDirectionalInput();
      target.focus();
      /**
       * Przewijamy do środka, nie „najkrótszą drogą".
       *
       * Domyślne `scrollIntoView` zatrzymuje element przy krawędzi ekranu, więc na
       * telewizorze kolejny kafel w prawo bywa już poza obrazem — a wtedy nie widać,
       * dokąd fokus pójdzie dalej.
       */
      target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

function toRect(element: HTMLElement): Rect {
  const box = element.getBoundingClientRect();
  return { left: box.left, top: box.top, right: box.right, bottom: box.bottom };
}

/**
 * Element widoczny, czyli mający jakikolwiek obszar.
 *
 * `offsetParent` nie wystarcza (elementy `position: fixed` go nie mają, a szyna
 * nawigacji i pasek dolny są przypięte), więc pytamy o prostokąt. Elementy ukryte
 * przez `display: none` mają zerowy rozmiar i wypadają same.
 */
function isVisible(element: HTMLElement): boolean {
  const box = element.getBoundingClientRect();
  return box.width > 0 && box.height > 0;
}
